import _extends from "@babel/runtime/helpers/extends";
import React from "react";
import { useSelector } from "react-redux";
import { AuthoritiesEnum, IntegrationTypeEnum, StatusStateEnum, ThemeTypeEnum } from "~/types/enums";
import { Alert, Box, Card, Picture, Text } from "~/components-shared";
import integrationSelectors from "~/store/integrations/selectors";
import SlackIntegrationCard from "~/components/SlackIntegrationCard";
import GoogleIntegrationCard from "~/components/GoogleIntegrationCard";
import NotionIntegrationCard from "~/components/NotionIntegrationCard";
import JiraIntegrationCard from "~/components/JiraIntegrationCard";
import AsanaIntegrationCard from "~/components/AsanaIntegrationCard";
import useAbacExt from "~/hooks/abac";
import companySelectors from "~/store/company/selectors";
import { useTranslation } from "react-i18next";
import LinearIntegrationCard from "~/components/LinearIntegrationCard";
import GithubIntegrationCard from "~/components/GithubIntegrationCard";
import SalesforceIntegrationCard from "~/components/SalesforceIntegrationCard";
import styled, { useTheme } from "styled-components";
import soc2Logo from "~/assets/i/logos/soc-2-rectangle-white.png";
import soc2LogoDark from "~/assets/i/logos/soc-2-rectangle-dark.png";
const StyledAlert = styled(Alert)`
  position: sticky;
  top: 1em;
  z-index: 2;
`;
const integrations = [{
  type: IntegrationTypeEnum.SLACK,
  component: /*#__PURE__*/React.createElement(SlackIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: [AuthoritiesEnum.INTEGRATIONS_READ, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE]
}, {
  type: IntegrationTypeEnum.GOOGLE,
  component: /*#__PURE__*/React.createElement(GoogleIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: [AuthoritiesEnum.INTEGRATIONS_READ, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE]
}, {
  type: IntegrationTypeEnum.NOTION,
  component: /*#__PURE__*/React.createElement(NotionIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: [AuthoritiesEnum.INTEGRATIONS_READ, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE]
}, {
  type: IntegrationTypeEnum.JIRA,
  component: /*#__PURE__*/React.createElement(JiraIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: [AuthoritiesEnum.INTEGRATIONS_READ, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE]
}, {
  type: IntegrationTypeEnum.LINEAR,
  component: /*#__PURE__*/React.createElement(LinearIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: [AuthoritiesEnum.INTEGRATIONS_READ, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE]
}, {
  type: IntegrationTypeEnum.ASANA,
  component: /*#__PURE__*/React.createElement(AsanaIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: [AuthoritiesEnum.INTEGRATIONS_READ, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE]
}, {
  type: IntegrationTypeEnum.GITHUB,
  component: /*#__PURE__*/React.createElement(GithubIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: [AuthoritiesEnum.INTEGRATIONS_READ, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE]
}, {
  type: IntegrationTypeEnum.SALESFORCE,
  component: /*#__PURE__*/React.createElement(SalesforceIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: [AuthoritiesEnum.INTEGRATIONS_READ, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE]
}];
const CompanyIntegrations = _ref => {
  let {
    filter,
    ...rest
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    type
  } = useTheme();
  const {
    userHasOneOf
  } = useAbacExt();
  const status = useSelector(integrationSelectors.getStatus);
  const integrationError = useSelector(integrationSelectors.getError);
  const memberError = useSelector(integrationSelectors.getMemberError);
  const memberStatus = useSelector(integrationSelectors.getMemberStatus);
  const company = useSelector(companySelectors.getCompany);
  const recommendedIntegrations = [];
  const optionalIntegrations = [];
  const error = integrationError || memberError;
  integrations.forEach(integration => {
    if (filter && filter.length > 0 && !filter.includes(integration.type)) {
      return;
    }
    if (company?.settings.recommendedIntegrations?.includes(integration.type)) {
      recommendedIntegrations.push(integration);
    } else {
      optionalIntegrations.push(integration);
    }
  });
  return /*#__PURE__*/React.createElement(Card, _extends({
    s: {
      p: 0
    },
    loading: status === StatusStateEnum.LOADING || memberStatus === StatusStateEnum.LOADING
  }, rest), error && /*#__PURE__*/React.createElement(StyledAlert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), !!recommendedIntegrations.length && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    element: "h6",
    s: {
      mb: 2
    },
    fontSize: 4
  }, t("CompanyIntegrations.recommended")), recommendedIntegrations.map(integration => userHasOneOf(integration.auth) && /*#__PURE__*/React.createElement(React.Fragment, {
    key: integration.type
  }, integration.component))), !!optionalIntegrations.length && /*#__PURE__*/React.createElement(React.Fragment, null, !!recommendedIntegrations.length && /*#__PURE__*/React.createElement(Text, {
    element: "h6",
    s: {
      my: 2
    },
    fontSize: 4
  }, t("CompanyIntegrations.optional")), optionalIntegrations.map(integration => userHasOneOf(integration.auth) && /*#__PURE__*/React.createElement(React.Fragment, {
    key: integration.type
  }, integration.component))), /*#__PURE__*/React.createElement(Box, {
    justify: "center",
    s: {
      mt: 8
    }
  }, /*#__PURE__*/React.createElement(Picture, {
    src: type === ThemeTypeEnum.LIGHT ? soc2LogoDark : soc2Logo,
    width: "200px",
    height: "72px",
    border: "squared",
    style: {
      opacity: 0.5
    }
  })));
};
export default CompanyIntegrations;